import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Clock } from 'lucide-react';
import { defaultScheduleContent } from '../../data/pageContentDefaults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Timeline = () => {
  const [c, setC] = useState(defaultScheduleContent);

  useEffect(() => {
    let alive = true;
    const fetchContent = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/schedule/content`, { params: { _: Date.now() } });
        if (!alive || !res.data) return;
        setC({ ...defaultScheduleContent, ...res.data });
      } catch { /* keep defaults */ }
    };
    fetchContent();
    const interval = setInterval(fetchContent, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchContent(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  const schedule = (() => {
    // Same defensive dedup + single-source render as the /event Timeline
    // section: dedup by `time + event`, preserve CMS-authored order. The
    // Admin → Schedule editor is the single canonical driver, so a stray
    // duplicate row cannot surface on this page after any redeployment.
    const raw = Array.isArray(c.items) ? c.items : [];
    const seen = new Set();
    return raw.filter((item) => {
      const key = `${(item.time || '').trim().toLowerCase()}|${(item.event || '').trim().toLowerCase()}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  return (
    <section id="timeline" className="section bg-cream" data-testid="timeline-section">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="font-cursive text-[#C8A951] text-2xl mb-2">{c.eyebrow}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#3B2F2F] tracking-tight">
            {c.title}
          </h2>
          <div className="gold-divider" />
        </motion.div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-[#C8A951]/30 transform md:-translate-x-1/2" />

          {schedule.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`relative flex flex-col md:flex-row items-start mb-8 ${
                index % 2 === 0 ? 'md:flex-row-reverse' : ''
              }`}
            >
              {/* Timeline dot */}
              <div className="absolute left-0 md:left-1/2 w-4 h-4 bg-[#C8A951] rounded-full transform md:-translate-x-1/2 -translate-y-0 z-10 shadow-lg" />

              {/* Content */}
              <div className={`ml-8 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                <div className="wedding-card">
                  <div className={`flex items-center gap-2 mb-2 ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                    <Clock className="w-4 h-4 text-[#C8A951]" />
                    <span className="text-[#C8A951] font-medium text-sm">{item.time}</span>
                  </div>
                  <h3 className="font-serif text-xl text-[#3B2F2F] mb-1">{item.event}</h3>
                  <p className="text-[#5A4A4A] text-sm">{item.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { Timeline };
